import React, { useEffect, useState } from 'react';
import { api } from '@/lib/db';
import { useToast } from '@/contexts/ToastContext';
import { Card, Button, Input } from '@/components/ui/Components';
import SafeIcon from '@/common/SafeIcon';

const SuperAdminLocations = () => {
  const { addToast } = useToast();
  const [locations, setLocations] = useState([]);
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const data = await api.getAllLocations();
      setLocations(data);
    } catch (err) {
      addToast('Hiba a betöltésnél', 'error');
    }
  };

  const openEdit = (loc) => {
    setEditForm({ ...loc });
    setEditModal(loc);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.updateLocation(editForm.id, editForm);
      addToast('Helyszín mentve!', 'success');
      setEditModal(null);
      fetchData();
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (loc) => {
    if (!confirm(`Biztosan törlöd ezt a helyszínt: "${loc.name}"?`)) return;
    try {
      await api.deleteLocation(loc.id);
      addToast('Helyszín törölve', 'success');
      fetchData();
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const filtered = locations.filter(loc =>
    loc.name.toLowerCase().includes(search.toLowerCase()) ||
    loc.business_name.toLowerCase().includes(search.toLowerCase()) ||
    loc.slug.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-black text-gray-900">Összes helyszín</h1>
          <p className="text-gray-500">Minden vállalkozás helyszíneinek kezelése.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <SafeIcon name="Search" className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Keresés..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <span className="text-sm text-gray-500">{filtered.length} helyszín</span>
        </div>
      </div>

      {/* ─── Szerkesztő modal ─────────────────────────────────────────── */}
      {editModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl p-8 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold">Helyszín szerkesztése</h2>
                <p className="text-sm text-gray-500">{editModal.business_name}</p>
              </div>
              <button onClick={() => setEditModal(null)} className="text-gray-400 hover:text-gray-600">
                <SafeIcon name="X" className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Megnevezés"
                  value={editForm.name || ''}
                  onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                  required
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Slug <span className="text-orange-500 text-xs font-normal">⚠ QR kódokat érinti!</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-400 font-mono">/r/</span>
                    <input
                      className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                      value={editForm.slug || ''}
                      onChange={e => setEditForm({ ...editForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                    />
                  </div>
                </div>
              </div>
              <Input
                label="Google Értékelés Link"
                value={editForm.google_review_url || ''}
                onChange={e => setEditForm({ ...editForm, google_review_url: e.target.value })}
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Logó URL"
                  value={editForm.logo_url || ''}
                  onChange={e => setEditForm({ ...editForm, logo_url: e.target.value })}
                />
                <div>
                  <Input
                    label="Márkaszín"
                    type="color"
                    className="h-10"
                    value={editForm.accent_color || '#ef4444'}
                    onChange={e => setEditForm({ ...editForm, accent_color: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Főcímsor"
                  value={editForm.headline || ''}
                  onChange={e => setEditForm({ ...editForm, headline: e.target.value })}
                />
                <Input
                  label="Leírás"
                  value={editForm.description || ''}
                  onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <Button type="button" variant="outline" onClick={() => setEditModal(null)}>Mégse</Button>
                <Button type="submit" disabled={saving}>{saving ? 'Mentés...' : 'Mentés'}</Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* ─── Táblázat ─────────────────────────────────────────────────── */}
      <Card className="overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Helyszín</th>
              <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Vállalkozás</th>
              <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Slug</th>
              <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Google Link</th>
              <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Műveletek</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="px-6 py-10 text-center text-gray-400 text-sm">
                  Nincsenek helyszínek
                </td>
              </tr>
            )}
            {filtered.map(loc => (
              <tr key={loc.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    {loc.logo_url ? (
                      <img src={loc.logo_url} alt="" className="w-8 h-8 rounded-lg object-cover border border-gray-200" />
                    ) : (
                      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                        <SafeIcon name="MapPin" className="w-4 h-4 text-gray-400" />
                      </div>
                    )}
                    <p className="font-semibold text-gray-900">{loc.name}</p>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <p className="text-sm text-gray-600">{loc.business_name}</p>
                </td>
                <td className="px-6 py-4">
                  <code className="text-xs font-mono text-blue-600 bg-blue-50 px-2 py-1 rounded">/r/{loc.slug}</code>
                </td>
                <td className="px-6 py-4">
                  {loc.google_review_url ? (
                    <a
                      href={loc.google_review_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline truncate max-w-[180px] block"
                    >
                      {loc.google_review_url.length > 40
                        ? loc.google_review_url.substring(0, 40) + '…'
                        : loc.google_review_url}
                    </a>
                  ) : (
                    <span className="text-gray-400 text-sm">—</span>
                  )}
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => openEdit(loc)}>
                      <SafeIcon name="Edit2" className="w-4 h-4" />
                    </Button>
                    <button
                      onClick={() => handleDelete(loc)}
                      className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Törlés"
                    >
                      <SafeIcon name="Trash2" className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

export default SuperAdminLocations;
